import React, { useState } from 'react';

import { Navigate } from 'react-router-dom';

import Button from 'components/modules/button/Button';
import GradeClassNumberLabel from 'components/modules/formats/GradeClassNumberLabel';
import Checkbox from 'components/modules/forms/Checkbox';
import ErrorList from 'components/modules/forms/ErrorList';
import DescriptionList from 'components/modules/lists/DescriptionList';
import DescriptionDetails from 'components/modules/lists/descriptionList/DescriptionDetails';
import DescriptionListItem from 'components/modules/lists/descriptionList/DescriptionListItem';
import DescriptionTerm from 'components/modules/lists/descriptionList/DescriptionTerm';
import useReactQuery from 'hooks/common/useReactQuery';
import { AccountRegistrationFormValues } from 'interfaces/registration';

import CSSModule from './AccountRegistrationConfirmation.module.scss';

interface Props {
  formValues: AccountRegistrationFormValues;
  isPC: boolean;
  onClickAccountRegistration: () => void;
}
interface EmailRegistrationTokenResponse {
  parent_email_registration_token_id: string;
}
const AccountRegistrationConfirmation: React.FC<Props> = ({
  formValues,
  isPC,
  onClickAccountRegistration,
}) => {
  const [isOrganizationMember, setIsOrganizationMember] = useState(false);

  const { useMutation } = useReactQuery();
  const { mutate, isPending, isError, error } =
    useMutation<EmailRegistrationTokenResponse>({
      method: 'POST',
      url: '/api/school/parents_v3/email_registration_tokens',
    });

  const is500Error = isError && error?.status === 500;
  const hasNon500Error = isError && !is500Error;

  const errors = !isError
    ? []
    : is500Error
      ? ['予期しないエラーが発生しました。時間をおいて再度お試しください']
      : [
          '予期しないエラーが発生し、登録できませんでした\r\n入力内容の確認をしてください',
        ];

  const buttonDisabled = !isOrganizationMember || isPending || hasNon500Error;

  const handleSubmit = () => {
    // リクエストボディを作成
    const { password_confirmation: _, ...requestBody } = {
      ...formValues,
      // TODO
      // parent_email_registration_token_idを付与する対応は別issue
      // https://github.com/quipper/school-comm-issues/issues/5605
    };

    mutate(JSON.stringify(requestBody), {
      onSuccess: () => {
        onClickAccountRegistration();
      },
    });
  };

  // NOTE: 団体会員コードから取得するAPIとの繋ぎこみ後に実装する
  const organizationName = '○○高校';
  const studentName = 'スタディ 太郎';
  const gradeLabel = '高1';
  const classNameLabel = '1';
  const attendanceNumberLabel = 1;

  // フォーム情報に一つでも欠損がないかを確認する
  const isAnyFormValuesNotFilled = Object.values(formValues).some(
    (value) => value === '' || value === null,
  );

  return isAnyFormValuesNotFilled ? (
    <Navigate replace to="/registration" />
  ) : (
    <>
      <div
        className={
          isPC
            ? CSSModule.AccountRegistrationConfirmation
            : CSSModule.AccountRegistrationConfirmation__Mobile
        }
      >
        <DescriptionList>
          <DescriptionListItem
            className={
              isPC
                ? CSSModule.DescriptionListItem
                : CSSModule.DescriptionListItem__Mobile
            }
          >
            <DescriptionTerm>団体会員コード</DescriptionTerm>
            <DescriptionDetails>{formValues.code}</DescriptionDetails>
          </DescriptionListItem>
          <DescriptionListItem
            className={
              isPC
                ? CSSModule.DescriptionListItem
                : CSSModule.DescriptionListItem__Mobile
            }
          >
            <DescriptionTerm>保護者氏名</DescriptionTerm>
            <DescriptionDetails>
              {formValues.last_name}
              &nbsp; &nbsp;
              {formValues.first_name}
            </DescriptionDetails>
          </DescriptionListItem>
          <DescriptionListItem
            className={
              isPC
                ? CSSModule.DescriptionListItem
                : CSSModule.DescriptionListItem__Mobile
            }
          >
            <DescriptionTerm>保護者氏名（かな）</DescriptionTerm>
            <DescriptionDetails>
              {formValues.last_name_kana}
              &nbsp; &nbsp;
              {formValues.first_name_kana}
            </DescriptionDetails>
          </DescriptionListItem>
          <DescriptionListItem
            className={
              isPC
                ? CSSModule.DescriptionListItem
                : CSSModule.DescriptionListItem__Mobile
            }
          >
            <DescriptionTerm>メールアドレス</DescriptionTerm>
            <DescriptionDetails>{formValues.email}</DescriptionDetails>
          </DescriptionListItem>
          <DescriptionListItem
            className={
              isPC
                ? CSSModule.DescriptionListItem
                : CSSModule.DescriptionListItem__Mobile
            }
          >
            <DescriptionTerm>パスワード</DescriptionTerm>
            <DescriptionDetails>●●●●●●●●●</DescriptionDetails>
          </DescriptionListItem>
        </DescriptionList>
      </div>

      <div className={CSSModule.OrganizationMemberInformation}>
        <div
          className={
            CSSModule.OrganizationMemberInformation__StudentInformation
          }
        >
          <div
            className={
              CSSModule.OrganizationMemberInformation__StudentInformation__OrganizationName
            }
          >
            {organizationName}
          </div>
          <GradeClassNumberLabel
            gradeLabel={gradeLabel}
            classNameLabel={classNameLabel}
            attendanceNumberLabel={attendanceNumberLabel}
          />
          <div
            className={
              CSSModule.OrganizationMemberInformation__StudentInformation__StudentName
            }
          >
            {studentName}
          </div>
        </div>

        <div className={CSSModule.OrganizationMemberInformation__CheckBokArea}>
          <Checkbox
            checked={isOrganizationMember}
            onChange={() => setIsOrganizationMember(!isOrganizationMember)}
            disabled={isPending || hasNon500Error}
            role="checkbox"
          >
            <div
              className={
                CSSModule.OrganizationMemberInformation__Checkbox__Text
              }
            >
              私はこの生徒の保護者であることを確認しました
            </div>
          </Checkbox>
        </div>
      </div>
      <div className={CSSModule.SubmitButtonArea}>
        <ul className={CSSModule.SubmitButtonArea__Note}>
          <li>
            生徒情報（年・組・番・氏名）が誤っている場合は、学校にお問い合わせするか、戻るボタンより団体会員コードを再設定してください
          </li>
        </ul>
        {errors.length > 0 && (
          <ErrorList
            className={CSSModule.SubmitButtonArea__Error}
            errors={errors}
          />
        )}
        <Button
          className={CSSModule.SubmitButton}
          color="primary"
          type="submit"
          disabled={buttonDisabled}
          onClick={handleSubmit}
        >
          仮登録をする
        </Button>
      </div>
    </>
  );
};

export default AccountRegistrationConfirmation;
